import { motion } from 'framer-motion'
import { features, caseStudies } from '../data'
import { Shield, BarChart2, Zap, Brain, Activity } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, PreviewableImage } from '../components/common'

export function SolutionsPage() {
  const getIcon = (icon: string) => {
    switch (icon) {
      case 'scan': return <Activity className="w-8 h-8 text-primary-500" />;
      case 'brain': return <Brain className="w-8 h-8 text-primary-500" />;
      case 'activity': return <Activity className="w-8 h-8 text-primary-500" />;
      case 'shield-check': return <Shield className="w-8 h-8 text-primary-500" />;
      case 'zap': return <Zap className="w-8 h-8 text-primary-500" />;
      case 'bar-chart-2': return <BarChart2 className="w-8 h-8 text-primary-500" />;
      default: return null;
    }
  }

  return (
    <>
      <section className="relative h-60 bg-gray-900 text-white">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(/images/solutions-banner.jpg)` }}></div>
        <div className="container-custom h-full flex items-center relative z-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">解决方案</h1>
            <p className="text-lg text-white/80">为教体局、学校、供货商、财务四方提供全方位信息化管理解决方案</p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              四大用户群体解决方案
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              书香云库为教体局、乡镇财务、学校食堂、供货商四方提供针对性的信息化管理解决方案
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-4">
                      {getIcon(feature.icon)}
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {feature.details && (
                      <ul className="space-y-2">
                        {feature.details.map((detail, i) => (
                          <li key={i} className="flex items-center space-x-2 text-sm text-gray-600">
                            <span className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              客户案例研究
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              看看我们的客户如何通过使用书香云库管理系统提升效率、降低成本
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((caseStudy, index) => (
              <motion.div
                key={caseStudy.id}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden">
                  <div className="h-64 bg-gray-200 overflow-hidden">
                    <PreviewableImage
                      src={caseStudy.image}
                      alt={caseStudy.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-primary-500">{caseStudy.industry}</span>
                      <span className="text-sm text-gray-500">{caseStudy.client}</span>
                    </div>
                    <CardTitle>{caseStudy.title}</CardTitle>
                    <CardDescription>{caseStudy.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {caseStudy.results.map((result, i) => (
                        <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">{result.metric}</p>
                          <p className="text-lg font-bold text-primary-500">{result.value}</p>
                          <p className="text-xs text-gray-500">{result.improvement}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
